import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Calendar, Clock, CheckCircle, AlertCircle, X, User, MapPin, 
  Wrench, ClipboardCheck, ChevronLeft, ChevronRight, Building2, Home
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  { value: 'morning', label: 'Morning (8AM - 12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM - 4PM)' },
  { value: 'evening', label: 'Evening (4PM - 7PM)' }
];

const serviceCategories = [
  { value: 'vinyl_fence', label: 'Vinyl Fence Installation' },
  { value: 'chain_link', label: 'Chain Link Fence' },
  { value: 'ornamental', label: 'Ornamental Iron/Aluminum' },
  { value: 'wood_fence', label: 'Wood Fence' },
  { value: 'fence_repair', label: 'Fence Repair' },
  { value: 'gate_install', label: 'Gate Installation' },
  { value: 'commercial', label: 'Commercial Fencing' },
  { value: 'property_services', label: 'Property Services' },
  { value: 'consultation', label: 'General Consultation' },
];

const provinces = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'ON', label: 'Ontario' },
  { value: 'QC', label: 'Quebec' },
];

const leadSources = [
  { value: 'google', label: 'Google Search' },
  { value: 'referral', label: 'Referral' },
  { value: 'social', label: 'Social Media' },
  { value: 'drive_by', label: 'Drove By / Signage' },
  { value: 'repeat', label: 'Repeat Customer' },
  { value: 'other', label: 'Other' },
];

const steps = [
  { id: 1, name: 'Contact', icon: User },
  { id: 2, name: 'Location', icon: MapPin },
  { id: 3, name: 'Service', icon: Wrench },
  { id: 4, name: 'Schedule', icon: Calendar },
  { id: 5, name: 'Review', icon: ClipboardCheck },
];

const BookingForm = ({ serviceType = 'general', serviceName = '', onClose }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const isPsBrand = location.pathname.startsWith('/ps');
  const [enableResidentialPS, setEnableResidentialPS] = useState(true);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    customer_type: 'individual',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: 'AB',
    postal_code: '',
    service_category: serviceType !== 'general' ? serviceType : '',
    preferred_date: '',
    preferred_time: 'morning',
    alternate_date: '',
    notes: '',
    lead_source: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const colors = {
    primary: '#456CAC',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
    inputBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  };

  // Fetch site settings for residential toggle
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-settings`);
        if (res.ok) {
          const data = await res.json();
          const enabled = data?.service_settings?.ps_residential_enabled ?? true;
          setEnableResidentialPS(enabled);
          // If PS brand and residential disabled, default to business
          if (isPsBrand && !enabled) {
            setFormData(prev => ({ ...prev, customer_type: 'business' }));
          }
        }
      } catch (err) {
        // Default to enabled
      }
    };
    fetchSettings();
  }, [isPsBrand]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') || params.get('ref');
    if (source) {
      setFormData(prev => ({ ...prev, lead_source: source }));
    }
  }, []);

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Combine first/last name for backend compatibility
      const submitData = {
        ...formData,
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        service_type: serviceType,
        service_name: serviceName || serviceCategories.find(s => s.value === formData.service_category)?.label || ''
      };
      
      await axios.post(`${API}/bookings`, submitData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = (step) => {
    setError('');
    switch (step) {
      case 1:
        if (!formData.first_name || !formData.email) {
          setError('Please fill in required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email');
          return false;
        }
        return true;
      case 2:
        if (!formData.address || !formData.city || !formData.postal_code) {
          setError('Please fill in your address details');
          return false;
        }
        return true;
      case 3:
        if (!formData.service_category) {
          setError('Please select a service');
          return false;
        }
        return true;
      case 4:
        if (!formData.preferred_date) {
          setError('Please select a preferred date');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const inputStyle = {
    backgroundColor: colors.inputBg,
    border: `1px solid ${colors.border}`,
    color: colors.text
  };

  if (submitted) {
    return (
      <div className="rounded-xl p-8 text-center" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
          Booking Request Submitted!
        </h3>
        <p style={{ color: colors.textMuted }} className="mb-2">
          Thank you for scheduling a site visit with us.
        </p>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          We'll contact you within 24 hours to confirm your appointment.
        </p>
        {onClose && (
          <Button onClick={onClose} className="mt-6" style={{ backgroundColor: colors.primary, color: '#fff' }}>
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl p-6" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold" style={{ color: colors.text }}>
            Schedule a Site Visit
          </h3>
          {serviceName && <p className="text-sm" style={{ color: colors.primary }}>{serviceName}</p>}
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#456CAC]/10">
            <X className="w-5 h-5" style={{ color: colors.textMuted }} />
          </button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  currentStep >= step.id ? 'text-white' : ''
                }`}
                style={{ 
                  backgroundColor: currentStep >= step.id ? colors.primary : colors.inputBg,
                  color: currentStep >= step.id ? '#fff' : colors.textMuted
                }}
              >
                <step.icon className="w-5 h-5" />
              </div>
              <span className="text-xs mt-1 hidden sm:block" style={{ color: currentStep >= step.id ? colors.primary : colors.textMuted }}>
                {step.name}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div 
                className="w-8 sm:w-16 h-0.5 mx-1"
                style={{ backgroundColor: currentStep > step.id ? colors.primary : colors.border }}
              />
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-500 text-sm">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Step 1: Contact Information */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Contact Information</h4>
            
            {/* Customer Type Toggle - Hide Homeowner for PS if disabled */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textMuted }}>
                I am a...
              </label>
              <div className="flex gap-3">
                {/* Only show Homeowner if not PS brand OR if residential PS is enabled */}
                {(!isPsBrand || enableResidentialPS) && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, customer_type: 'individual' })}
                    className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                      formData.customer_type === 'individual' ? 'ring-2 ring-[#456CAC]' : ''
                    }`}
                    style={{ 
                      ...inputStyle,
                      backgroundColor: formData.customer_type === 'individual' ? `${colors.primary}15` : colors.inputBg
                    }}
                    data-testid="booking-homeowner-btn"
                  >
                    <Home className="w-5 h-5" style={{ color: formData.customer_type === 'individual' ? colors.primary : colors.textMuted }} />
                    <span style={{ color: formData.customer_type === 'individual' ? colors.primary : colors.text }}>Homeowner</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, customer_type: 'business' })}
                  className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    formData.customer_type === 'business' ? 'ring-2 ring-[#456CAC]' : ''
                  }`}
                  style={{ 
                    ...inputStyle,
                    backgroundColor: formData.customer_type === 'business' ? `${colors.primary}15` : colors.inputBg
                  }}
                  data-testid="booking-business-btn"
                >
                  <Building2 className="w-5 h-5" style={{ color: formData.customer_type === 'business' ? colors.primary : colors.textMuted }} />
                  <span style={{ color: formData.customer_type === 'business' ? colors.primary : colors.text }}>Business</span>
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  data-testid="booking-first-name-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  Last Name
                </label>
                <input
                  type="text"
                  data-testid="booking-last-name-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  data-testid="booking-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  Phone
                </label>
                <input
                  type="tel"
                  data-testid="booking-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="(403) 555-1234"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Location */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Property Location</h4>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                Street Address *
              </label>
              <input
                type="text"
                required
                data-testid="booking-address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={inputStyle}
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  City *
                </label>
                <input
                  type="text"
                  required
                  data-testid="booking-city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="Red Deer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  Province *
                </label>
                <select
                  required
                  data-testid="booking-province-input"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                >
                  {provinces.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  Postal Code *
                </label>
                <input
                  type="text"
                  required
                  data-testid="booking-postal-input"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.toUpperCase() })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                  placeholder="T4N 1A1"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Service Selection */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Select Service</h4>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceCategories.map(service => (
                <button
                  key={service.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, service_category: service.value })}
                  className={`p-4 rounded-lg text-left transition-colors ${
                    formData.service_category === service.value ? 'ring-2 ring-[#456CAC]' : ''
                  }`}
                  style={{ 
                    ...inputStyle,
                    backgroundColor: formData.service_category === service.value ? `${colors.primary}15` : colors.inputBg
                  }}
                >
                  <span style={{ color: formData.service_category === service.value ? colors.primary : colors.text }}>
                    {service.label}
                  </span>
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                How did you hear about us?
              </label>
              <select
                data-testid="booking-source-input"
                value={formData.lead_source}
                onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={inputStyle}
              >
                <option value="">Select...</option>
                {leadSources.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 4: Schedule */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Select Date & Time</h4>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Preferred Date *
                </label>
                <input
                  type="date"
                  required
                  data-testid="booking-date-input"
                  min={getMinDate()}
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                  <Clock className="w-4 h-4 inline mr-1" />
                  Preferred Time *
                </label>
                <select
                  required
                  data-testid="booking-time-input"
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                  className="w-full p-3 rounded-lg"
                  style={inputStyle}
                >
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                Alternate Date (optional)
              </label>
              <input
                type="date"
                min={getMinDate()}
                data-testid="booking-alt-date-input"
                value={formData.alternate_date}
                onChange={(e) => setFormData({ ...formData, alternate_date: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
                Additional Notes
              </label>
              <textarea
                rows={3}
                data-testid="booking-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full p-3 rounded-lg resize-none"
                style={inputStyle}
                placeholder="Any special instructions, gate codes, or details about your project..."
              />
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Review Your Booking</h4>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <h5 className="font-medium mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                  <User className="w-4 h-4" /> Contact
                </h5>
                <p style={{ color: colors.text }}>
                  {formData.first_name} {formData.last_name} ({formData.customer_type})
                </p>
                <p style={{ color: colors.textMuted }}>{formData.email}</p>
                {formData.phone && <p style={{ color: colors.textMuted }}>{formData.phone}</p>}
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <h5 className="font-medium mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                  <MapPin className="w-4 h-4" /> Location
                </h5>
                <p style={{ color: colors.text }}>{formData.address}</p>
                <p style={{ color: colors.textMuted }}>
                  {formData.city}, {formData.province} {formData.postal_code}
                </p>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <h5 className="font-medium mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                  <Wrench className="w-4 h-4" /> Service
                </h5>
                <p style={{ color: colors.text }}>
                  {serviceCategories.find(s => s.value === formData.service_category)?.label || serviceName}
                </p>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                <h5 className="font-medium mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                  <Calendar className="w-4 h-4" /> Schedule
                </h5>
                <p style={{ color: colors.text }}>
                  {new Date(formData.preferred_date).toLocaleDateString('en-CA', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </p>
                <p style={{ color: colors.textMuted }}>
                  {timeSlots.find(t => t.value === formData.preferred_time)?.label}
                </p>
                {formData.alternate_date && (
                  <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
                    Alternate: {new Date(formData.alternate_date).toLocaleDateString('en-CA')}
                  </p>
                )}
              </div>

              {formData.notes && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                  <h5 className="font-medium mb-2" style={{ color: colors.primary }}>Notes</h5>
                  <p style={{ color: colors.textMuted }}>{formData.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < 5 ? (
            <Button
              type="button"
              onClick={nextStep}
              className="gap-2"
              style={{ backgroundColor: colors.primary, color: '#fff' }}
            >
              Continue
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={submitting}
              className="gap-2"
              style={{ backgroundColor: colors.primary, color: '#fff' }}
            >
              {submitting ? 'Submitting...' : 'Confirm Booking'}
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
        </div>

        {currentStep === 5 && (
          <p className="text-xs text-center mt-4" style={{ color: colors.textMuted }}>
            By confirming, you agree to our{' '}
            <a href="/privacy" className="underline" style={{ color: colors.primary }}>Privacy Policy</a>{' '}
            and{' '}
            <a href="/fence/terms/residential" className="underline" style={{ color: colors.primary }}>Terms of Service</a>.
            Booking requests are not confirmed until you receive email confirmation from our team.
          </p>
        )}
      </form>
    </div>
  );
};

export default BookingForm;
